import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Form, Badge } from 'react-bootstrap';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { CME_ADMIN_API } from '../../../../utils/apiUrl';
import { DeleteSingle, Get, Post } from '../../../../utils/httpProvider';
import { messageToast, status200 } from '../../../../utils/define';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';
import CMEConfigAddEditModal from './CMEConfigAddEditModal';

const CMEConfig = () => {
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');
    const [deleteType, setDeleteType] = useState('category'); // 'category' or 'rule'

    const [openModal, setOpenModal] = useState(false);
    const [modalType, setModalType] = useState('category');
    const [selectedData, setSelectedData] = useState(null);
    const [parentCatId, setParentCatId] = useState(null);

    // Fetch Categories
    const { isLoading, isError, error, data, refetch } = useQuery(
        ['cmeCategoriesData'],
        () => Get(CME_ADMIN_API.GET_CATEGORIES),
        { keepPreviousData: true }
    );

    const deleteCategoryFunc = useMutation((id) => DeleteSingle(`${CME_ADMIN_API.DELETE_CATEGORY}/${id}`));
    const deleteRuleFunc = useMutation((id) => DeleteSingle(`${CME_ADMIN_API.DELETE_RULE}/${id}`));

    const upsertCatFunc = useMutation((data) => Post(CME_ADMIN_API.ADD_UPDATE_CATEGORY, null, data));
    const upsertRuleFunc = useMutation((data) => Post(CME_ADMIN_API.ADD_UPDATE_RULE, null, data));

    const handleAddCat = () => {
        setModalType('category');
        setSelectedData(null);
        setOpenModal(true);
    };

    const handleEditCat = (cat) => {
        setModalType('category');
        setSelectedData(cat);
        setOpenModal(true);
    };

    const handleAddRule = (catId) => {
        setModalType('rule');
        setParentCatId(catId);
        setSelectedData(null);
        setOpenModal(true);
    };

    const handleEditRule = (rule) => {
        setModalType('rule');
        setSelectedData(rule);
        setOpenModal(true);
    };

    const saveConfig = (values) => {
        if (modalType === 'category') {
            const payload = { 
                ...selectedData, 
                maDanhMuc: values.ma,
                tenDanhMuc: values.ten
            };
            upsertCatFunc.mutate(payload, {
                onSuccess: (res) => {
                    if (res.status === status200 || res === true || res.id) {
                        toast.success(messageToast.messageToastUpdateSuccess);
                        refetch();
                        setOpenModal(false);
                    }
                }
            });
        } else {
            const payload = { 
                ...selectedData, 
                maTieuMuc: values.ma,
                tenTieuMuc: values.ten, 
                dinhMucGio: values.soGioToiDa,
                vaiTro: values.vaiTro,
                donViTinh: values.donViTinh,
                ghiChu: values.moTa,
                isAnnualRenewal: values.isAnnualRenewal,
                danhMucId: parentCatId || selectedData?.danhMucId
            };
            upsertRuleFunc.mutate(payload, {
                onSuccess: (res) => {
                    if (res.status === status200 || res === true || res.id) {
                        toast.success(messageToast.messageToastUpdateSuccess);
                        refetch();
                        setOpenModal(false);
                    }
                }
            });
        }
    };

    const deleteAction = (id, name, type) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name);
        setDeleteType(type);
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        if (!idWantDelete) return;

        const func = deleteType === 'category' ? deleteCategoryFunc : deleteRuleFunc;

        func.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res.status === status200 || res.status === 201 || res === true) {
                    toast.success(messageToast.messageToastDeleteSuccess);
                    refetch();
                } else {
                    toast.error(res?.message || messageToast.messageToastDeleteError);
                }
            },
            onError: (err) => {
                toast.error(err?.message || messageToast.messageToastTryCatch);
            }
        });

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const categories = data?.$values ? data.$values : data || [];

    return (
        <div className="container-fluid">
            <Card className="mb-4">
                <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0"><i className="fas fa-cogs mr-2"></i>Cấu hình Danh mục & Quy định CME</h5>
                    <Button variant="light" size="sm" onClick={handleAddCat}>
                        <i className="fas fa-plus mr-1"></i>Thêm Danh mục
                    </Button>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <Table bordered hover>
                            <thead className="thead-light">
                                <tr>
                                    <th style={{ width: '50px' }}>STT</th>
                                    <th>Danh mục / Tiểu mục (Quy định)</th>
                                    <th style={{ width: '150px' }}>Vai trò</th>
                                    <th style={{ width: '150px' }}>Cách tính</th>
                                    <th style={{ width: '150px' }}>Hành động</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={5} className="text-center">Đang tải...</td></tr>
                                ) : categories.length === 0 ? (
                                    <tr><td colSpan={5} className="text-center">Chưa có cấu hình nào</td></tr>
                                ) : (
                                    categories.map((cat, idx) => (
                                        <React.Fragment key={cat.id}>
                                            <tr className="bg-light font-weight-bold">
                                                <td>{idx + 1}</td>
                                                <td>{cat.tenDanhMuc}</td>
                                                <td>-</td>
                                                <td>-</td>
                                                <td>
                                                    <Button variant="link" className="text-primary p-0 mr-2" onClick={() => handleEditCat(cat)}>Sửa</Button>
                                                    <Button variant="link" className="text-danger p-0" onClick={() => deleteAction(cat.id, cat.tenDanhMuc, 'category')}>Xóa</Button>
                                                </td>
                                            </tr>
                                            {(cat.cmeTieuMucs?.$values || cat.cmeTieuMucs || []).map((rule, sIdx) => (
                                                <tr key={rule.id}>
                                                    <td></td>
                                                    <td className="pl-4"><i className="fas fa-angle-right mr-2 text-muted"></i>{rule.tenTieuMuc}</td>
                                                    <td>{rule.vaiTro || '-'}</td>
                                                    <td className="text-center"><Badge variant="info">{rule.dinhMucGio} giờ / {rule.donViTinh || 'đv'}</Badge></td>
                                                    <td>
                                                        <Button variant="link" size="sm" className="text-primary p-0 mr-2" onClick={() => handleEditRule(rule)}>Sửa</Button>
                                                        <Button variant="link" size="sm" className="text-danger p-0" onClick={() => deleteAction(rule.id, rule.tenTieuMuc, 'rule')}>Xóa</Button>
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr className="border-0">
                                                <td></td>
                                                <td colSpan={4} className="py-1">
                                                    <Button variant="outline-success" size="sm" className="ml-3" style={{ fontSize: '0.75rem' }} onClick={() => handleAddRule(cat.id)}>
                                                        <i className="fas fa-plus-circle mr-1"></i>Thêm tiểu mục cho "{cat.tenDanhMuc}"
                                                    </Button>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            <ConfirmModal
                isOpen={openConfirmDeleteModal}
                confirmType="delete"
                handleAccept={acceptDelete}
                handleCancel={() => setOpenConfirmDeleteModal(false)}
                content={`Bạn có chắc chắn muốn xóa ${deleteType === 'category' ? 'danh mục' : 'tiểu mục'}: "${confirmDelMessage}"?`}
            />

            <CMEConfigAddEditModal
                isOpen={openModal}
                type={modalType}
                handleClose={() => setOpenModal(false)}
                handleSave={saveConfig}
                data={selectedData}
            />
        </div>
    );
};

export default CMEConfig;
